import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MAIN_SITE_URL, SITE_URL } from '@/data/site';
import { isSupportedLanguage } from '@/data/languages';
import { getArticleBySlug } from '@/data/articles';
import JsonLd from '@/components/base/JsonLd';
import { articleSchema, breadcrumbListSchema, faqPageSchema } from '@/data/schema';

export default function Article() {
  const { t } = useTranslation();
  const location = useLocation();
  const { slug } = useParams();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const firstSegment = location.pathname.split('/').filter(Boolean)[0];
  const lang = isSupportedLanguage(firstSegment) ? firstSegment : 'en';

  const article = getArticleBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 pt-20 text-center">
        <p className="font-heading text-2xl font-semibold text-foreground-950">Guide not found</p>
        <Link
          to={`/${lang}`}
          className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          {t('article.breadcrumbHome')}
        </Link>
      </div>
    );
  }

  const faqHeading = t('article.faq.heading');

  const sections = article.sections.map((section, index) => ({
    id: `section-${index}`,
    label: section.heading,
  }));

  const articleUrl = `${SITE_URL}/${lang}/${article.slug}`;
  const articleTitle = t(`pillars.${article.key}.title`);
  const articleDescription = t(`pillars.${article.key}.desc`);
  const breadcrumbs = [
    { name: t('article.breadcrumbHome'), url: `${SITE_URL}/${lang}` },
    { name: articleTitle, url: articleUrl },
  ];

  return (
    <article className="pt-20 md:pt-24">
      <JsonLd
        data={articleSchema({
          headline: articleTitle,
          description: articleDescription,
          image: article.image,
          url: articleUrl,
          datePublished: '2026-08-01',
          dateModified: '2026-08-19',
          author: t('article.author'),
        })}
      />
      <JsonLd data={breadcrumbListSchema(breadcrumbs)} />
      <JsonLd data={faqPageSchema(article.faq)} />

      <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="text-sm text-foreground-500">
          <ol className="flex flex-wrap items-center gap-1.5">
            <li>
              <Link to={`/${lang}`} className="transition-colors hover:text-primary-700">
                {t('article.breadcrumbHome')}
              </Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line text-base leading-none"></i>
            </li>
            <li className="text-foreground-700">{t(`pillars.${article.key}.title`)}</li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mx-auto mt-8 max-w-3xl text-center">
          <h1 className="font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-4xl">
            {t(`pillars.${article.key}.title`)}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {t(`pillars.${article.key}.desc`)}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-foreground-500">
            <span>
              {t('article.authorLabel')} <strong className="font-medium text-foreground-800">{t('article.author')}</strong>
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-sm leading-none"></i>
              {article.readTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-sm leading-none"></i>
              {t('article.updatedLabel')}: {t('article.updated')}
            </span>
          </div>
        </header>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_260px]">
          {/* Main content */}
          <div className="min-w-0 space-y-12">
            {article.sections.map((section, index) => (
              <section key={section.heading} id={`section-${index}`} className="scroll-mt-24">
                <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                  {section.heading}
                </h2>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph} className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                    {paragraph}
                  </p>
                ))}
                {section.list && (
                  <ul className="mt-4 space-y-3">
                    {section.list.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-foreground-700 md:text-base">
                        <i className="ri-checkbox-circle-line mt-0.5 text-lg leading-none text-primary-600"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            {/* FAQ */}
            <section id="faq" className="scroll-mt-24">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">{faqHeading}</h2>
              <div className="mt-5 space-y-3">
                {article.faq.map((item, index) => {
                  const isOpen = openFaq === index;
                  return (
                    <div key={item.q} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : index)}
                        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                      >
                        <span className="text-sm font-medium text-foreground-950">{item.q}</span>
                        <i
                          className={`ri-add-line text-lg leading-none text-foreground-500 transition-transform ${
                            isOpen ? 'rotate-45' : ''
                          }`}
                        ></i>
                      </button>
                      {isOpen && (
                        <div className="px-5 pb-4">
                          <p className="text-sm leading-relaxed text-foreground-700">{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* CTA with internal links */}
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 text-center md:p-8">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                {t('article.cta.title')}
              </h2>
              <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-foreground-700 md:text-base">
                {t('article.cta.desc')}
              </p>
              <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link
                  to={`/${lang}`}
                  className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
                >
                  {t('article.breadcrumbHome')}
                </Link>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-background-100"
                >
                  {t('buttons.visitMainSite')}
                </a>
              </div>
            </div>
          </div>

          {/* TOC sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500">
                {t('article.tocLabel')}
              </p>
              <nav className="mt-4 flex flex-col gap-1">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                  >
                    {section.label}
                  </a>
                ))}
                <a
                  href="#faq"
                  className="rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                >
                  {faqHeading}
                </a>
              </nav>
            </div>
          </aside>
        </div>
      </div>
    </article>
  );
}